import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import {
	ContentMessage,
	DataTable
} from 'axway-react-ui';
import { Button } from 'react-bootstrap';

class ComponentsTabView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
	}

	/**
	 * Renders a new ContentMessage with the corresponding no results message:
	 * - If a search filter was applied it states no matches was found
	 * - Default message for the case there were simply no components.
	 */
	renderEmpty() {
		const { searchFilter, type } = this.props;
		// The default no components message
		const noPlugins = this.intl.formatMsg('No plugins found.');
		// No components matched the resulted filter options
		const noFilteredResults = this.intl.formatMsg('Could not find any {type} matching \'{search}\'.', {
			type,
			search: searchFilter
		});
		return (
			<ContentMessage
				detail={searchFilter ? noFilteredResults : noPlugins}
				imgClass="gg icon-GG-empty-list"
				className="sm empty"
				inlineContent
			/>
		);
	}

	render() {
		const { components } = this.props;

		if (!components.length) {
			return (
				<div className="components-tab-view">
					{this.renderEmpty()}
				</div>
			);
		}

		const installButtonLabel = this.intl.formatMsg('Install');
		const uninstallButtonLabel = this.intl.formatMsg('Uninstall');
		const supportedByAxway = this.intl.formatMsg('Supported by Axway');
		const communityPlugin = this.intl.formatMsg('Community plugin');
		const reportIssues = this.intl.formatMsg('Report issues');

		const columns = [
			{
				key: 'type',
				className: 'component-type',
				format: (item) => {
					const icons = {
						plugin: 'site-map',
						connector: 'database',
						trigger: 'request'
					};

					return (
						<span className={`gg icon-GG-${icons[item.type]}`} />
					);
				}
			},
			{
				key: 'info',
				className: 'component-info',
				format: (item) => {
					const isWIP = item.tags.includes('wip');
					return (
						<div>
							{item.docs ? (
								<a
									href={item.docs}
									className="component-name"
									target="_blank"
									// Using target="_blank" without
									// rel="noopener" is a security risk:
									// https://html.spec.whatwg.org/multipage/links.html#link-type-noopener
									rel="noopener noreferrer"
								>
									{item.name}
								</a>
							) : (
								<span className="component-name">{item.name}</span>
							)}
							{ isWIP && (
								<span
									className="wip-warning gg icon-GG-warning"
									title="Work in Progress"
								/>
							)}
							<div className="component-package-name">
								<span>{item.package} {item.currentVersion}</span>
							</div>
							<div>{item.description}</div>
						</div>
					);
				}
			},
			{
				key: 'tags',
				className: item => {
					if (!item.installed) {
						return 'component-tags hidden';
					}
					return 'component-tags';
				},
				format: (item) => {
					if (!item.installed) {
						return null;
					}
					const isAxway = item.tags.includes('axway');

					const icon = isAxway ? 'icon-GG-axway-new' : 'icon-GG-github';
					return (
						<div className="component-tag">
							<span className={`gg ${icon}`} />
							<div className="component-tag-text">
								{isAxway ? supportedByAxway : communityPlugin}
							</div>
							<div className="link">
								<a
									href={item.support}
									target="_blank"
									// Using target="_blank" without
									// rel="noopener" is a security risk:
									// https://html.spec.whatwg.org/multipage/links.html#link-type-noopener
									rel="noopener noreferrer"
								>
									{reportIssues}
								</a>
							</div>
						</div>
					);
				}
			},
			{
				key: 'action',
				className: 'component-actions',
				format: (item) => {
					return item.installed ? (
						<div className="uninstall">
							<Button
								bsSize="large"
								bsStyle="link"
								className="cancel"
								onClick={() => this.props.onUninstallRequest(item)}
							>
								{!item.usage.uninstallable
									&& <span className="gg icon-GG-warning" />}
								{uninstallButtonLabel}
							</Button>
						</div>
					) : (
						<div className="install">
							<Button
								bsSize="large"
								bsStyle="primary"
								className="compact"
								onClick={() => this.props.onInstallRequest(item)}
							>
								<span className="gg icon-GG-download2" />
								{installButtonLabel}
							</Button>
						</div>
					);
				}
			}
		];

		return (
			<div className="components-tab-view">
				<DataTable
					headers={false}
					columns={columns}
					items={components}
					className="ux-container components-list"
				/>
			</div>
		);
	}
}

ComponentsTabView.propTypes = {
	// i18n plugin type reflecting the tab name
	type: PropTypes.string.isRequired,
	// The current search
	searchFilter: PropTypes.string.isRequired,
	// Array of plugins
	components: PropTypes.array.isRequired,
	onInstallRequest: PropTypes.func.isRequired,
	onUninstallRequest: PropTypes.func.isRequired
};

export default injectIntl(ComponentsTabView);
